import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { buildPreviewModel } from "./buildPreviewModel";
import { coverElementImageSrc, cssObjectPosition, displayText, elementAlign, elementValign, ensureCoverInstructions, pageMetrics } from "./coverInstructions";
import { DEFAULT_PDF_SETTINGS, normalizePdfSettings } from "./pdfSettings";

const preloadImage = (src) =>
	new Promise((resolve) => {
		if (!src) {
			resolve();
			return;
		}
		const image = new Image();
		image.onload = () => resolve();
		image.onerror = () => resolve();
		image.src = src;
	});

const waitForDomImages = (root) => {
	if (!root) return Promise.resolve();
	const images = [...root.querySelectorAll("img")];
	if (images.length === 0) return Promise.resolve();
	return Promise.all(
		images.map(
			(image) =>
				image.complete
					? Promise.resolve()
					: new Promise((resolve) => {
							image.addEventListener("load", resolve, { once: true });
							image.addEventListener("error", resolve, { once: true });
					  }),
		),
	);
};

/**
 * Mock of the backend pdfmake body PDF.
 * Layout uses real A4 point sizes (1 CSS px ≈ 1pt), then scales the sheet to fit the UI
 * so field wrapping matches the generated report. With section separators on, each section
 * starts a new page. With them off, the next section continues on the current page.
 */

/** pdfmake A4 pageSize in points — preview lays out at this size, then scales for display. */
const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;
const SHEET_DISPLAY_MAX = 480;

const SHEET_FONT = {
	fontFamily: 'Times, "Times New Roman", serif',
	lineHeight: 1.2,
};

const FieldRow = ({
	field,
	highlighted,
	focusAttr,
	onSelect,
	showDivider = true,
	nameSize = 12,
	dataSize = 12,
	nameGap = 10,
	fieldSpace = 5,
	lineHeight = 1.2,
}) => (
	<button
		type="button"
		data-focus-key={focusAttr}
		onClick={() => onSelect?.(focusAttr)}
		style={{ marginBottom: fieldSpace, lineHeight }}
		className={`w-full text-left cursor-pointer rounded transition-colors ${
			highlighted ? "ring-2 ring-inset ring-amber-300 bg-amber-50/50" : "hover:bg-amber-50/50 hover:ring-2 hover:ring-inset hover:ring-amber-300"
		}`}>
		{showDivider ? <div className="border-t border-stone-800/40" style={{ marginTop: 8, marginBottom: 4 }} /> : null}
		{/* 25% / 75% matches pdfmake fieldName / fieldData column widths */}
		<div className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)] items-start" style={{ columnGap: nameGap }}>
			<p className="font-bold m-0" style={{ fontSize: nameSize, paddingTop: 10, overflowWrap: "normal", wordBreak: "normal" }}>
				{field.name}
			</p>
			<p className="m-0 whitespace-pre-wrap" style={{ fontSize: dataSize, paddingTop: 10, marginLeft: -7.4, overflowWrap: "normal", wordBreak: "normal" }}>
				{field.sampleValue}
			</p>
		</div>
		{field.showPhotos ? (
			<div className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)] mt-1" style={{ columnGap: nameGap }}>
				<div />
				<div className="flex flex-wrap gap-1.5">
					{[0, 1, 2, 3].map((i) => (
						<div
							key={i}
							className="bg-stone-100 border border-stone-300 flex items-center justify-center text-[8px] text-stone-400 shrink-0"
							style={{ width: 72, height: 96 }}>
							Photo
						</div>
					))}
				</div>
			</div>
		) : null}
		{field.showComment ? (
			<div className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)] items-start" style={{ columnGap: nameGap }}>
				<p className="font-bold m-0" style={{ fontSize: nameSize, paddingTop: 10 }}>
					Comment
				</p>
				<p className="m-0 whitespace-pre-wrap" style={{ fontSize: dataSize, paddingTop: 10, marginLeft: -7.4 }}>
					{field.comment}
				</p>
			</div>
		) : null}
	</button>
);

const clampNumber = (next, min, max, step) => {
	const snapped = Math.round(Number(next) / step) * step;
	const rounded = step < 1 ? Number(snapped.toFixed(2)) : Math.round(snapped);
	return Math.min(max, Math.max(min, rounded));
};

const FontSizeField = ({ label, value, onChange, min = 8, max = 72, step = 1 }) => {
	const rowRef = useRef(null);
	const scrub = useRef(null);

	useEffect(() => {
		const row = rowRef.current;
		if (!row) return;
		const onWheel = (event) => {
			event.preventDefault();
			const direction = event.deltaY < 0 ? 1 : -1;
			onChange(clampNumber(value + direction * step, min, max, step));
		};
		row.addEventListener("wheel", onWheel, { passive: false });
		return () => row.removeEventListener("wheel", onWheel);
	}, [value, onChange, min, max, step]);

	const onScrubDown = (event) => {
		if (event.button !== 0) return;
		event.preventDefault();
		event.currentTarget.setPointerCapture(event.pointerId);
		scrub.current = { startX: event.clientX, startValue: Number(value) || 0 };
	};

	const onScrubMove = (event) => {
		if (!scrub.current || !event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
		const delta = event.clientX - scrub.current.startX;
		onChange(clampNumber(scrub.current.startValue + delta * step, min, max, step));
	};

	const onScrubUp = (event) => {
		if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
		scrub.current = null;
	};

	return (
		<label ref={rowRef} className="flex items-center justify-between gap-2 text-sm">
			<span
				className="text-stone-600 cursor-ew-resize select-none"
				title="Drag left or right, or scroll, to change"
				onPointerDown={onScrubDown}
				onPointerMove={onScrubMove}
				onPointerUp={onScrubUp}
				onPointerCancel={onScrubUp}>
				{label}
			</span>
			<input
				type="number"
				min={min}
				max={max}
				step={step}
				value={value}
				title="Scroll to change"
				onChange={(e) => {
					const next = Number(e.target.value);
					if (!Number.isFinite(next)) return;
					onChange(clampNumber(next, min, max, step));
				}}
				className="w-16 border border-stone-300 rounded-md px-2 py-1 bg-white"
			/>
		</label>
	);
};

const PAGE_HEIGHT = A4_HEIGHT;
/** pdfmake draws the footer in the bottom page margin. That margin is 19pt. */
const FOOTER_BAND = 19;
/** Footer text is inset 10pt from the page edge, separate from the content margin. */
const FOOTER_INSET = 10;

/** 1px border on the top and bottom is inside the sheet chrome only (not layout math). */
const SHEET_BORDER = 0;

/** Room for fields above the footer band — matches pdfmake usable height. */
const contentHeightFor = (pageMargin) => Math.max(120, PAGE_HEIGHT - SHEET_BORDER - pageMargin - FOOTER_BAND);

/** offsetHeight leaves out margins, and those margins are what push the last field into the footer. */
const blockHeight = (node) => {
	if (!node) return 0;
	const style = window.getComputedStyle(node);
	const margin = (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
	return node.getBoundingClientRect().height + margin;
};

/** Invisible measure host at true A4 content width so wrapping matches pdfmake. */
const MeasureHost = ({ measureRef, pageMargin, lineHeight, children }) => (
	<div
		ref={measureRef}
		aria-hidden
		className="absolute left-0 top-0 invisible pointer-events-none overflow-hidden"
		style={{
			...SHEET_FONT,
			lineHeight,
			width: A4_WIDTH,
			paddingLeft: pageMargin,
			paddingRight: pageMargin,
			boxSizing: "border-box",
		}}>
		{children}
	</div>
);

const PdfSheet = ({ children, footerDate, pageLabel, pageMargin = 10, lineHeight = 1.2, thresholdPercent = null }) => {
	const hostRef = useRef(null);
	const [scale, setScale] = useState(SHEET_DISPLAY_MAX / A4_WIDTH);
	const contentHeight = Math.max(1, PAGE_HEIGHT - pageMargin - FOOTER_BAND);
	const showThreshold = thresholdPercent != null && Number.isFinite(Number(thresholdPercent));
	const thresholdValue = showThreshold ? Math.max(0, Math.min(50, Number(thresholdPercent))) : 0;
	const thresholdTop = PAGE_HEIGHT - FOOTER_BAND - (contentHeight * thresholdValue) / 100;

	useLayoutEffect(() => {
		const host = hostRef.current;
		if (!host) return;
		const measure = () => {
			const width = host.getBoundingClientRect().width;
			if (width > 2) setScale(width / A4_WIDTH);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(host);
		return () => observer.disconnect();
	}, []);

	return (
		<div ref={hostRef} className="relative shrink-0 w-full max-w-[480px]" style={{ height: A4_HEIGHT * scale }}>
			<div
				className="bg-white shadow-md border border-stone-300 relative overflow-hidden"
				style={{
					...SHEET_FONT,
					lineHeight,
					width: A4_WIDTH,
					height: A4_HEIGHT,
					transform: `scale(${scale})`,
					transformOrigin: "top left",
				}}>
				<div className="absolute overflow-hidden" style={{ top: pageMargin, right: pageMargin, bottom: FOOTER_BAND, left: pageMargin }}>
					{children}
				</div>
				{showThreshold ? (
					<div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: thresholdTop }} aria-hidden>
						<div className="border-t border-dashed border-amber-500" />
					</div>
				) : null}
				<div
					className="absolute flex justify-between items-start text-[11px]"
					style={{ left: FOOTER_INSET, right: FOOTER_INSET, bottom: 0, height: FOOTER_BAND }}>
					<span>{pageLabel}</span>
					<span>{footerDate}</span>
				</div>
			</div>
			{showThreshold ? (
				<span
					className="absolute left-full ml-2 -translate-y-1/2 z-10 rounded bg-amber-50 border border-amber-300 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 whitespace-nowrap shadow-sm pointer-events-none"
					style={{ top: thresholdTop * scale }}
					aria-hidden>
					New content threshold: {Math.round(thresholdValue)}%
				</span>
			) : null}
		</div>
	);
};

const coverFontFamily = (font = "") => (String(font).startsWith("Helvetica") ? "Helvetica, Arial, sans-serif" : 'Times, "Times New Roman", serif');

const CoverElement = ({ element, page, reportName, dateString, logos, brandLogo }) => {
	const style = {
		position: "absolute",
		left: `${(element.x / page.width) * 100}%`,
		top: `${(element.y / page.height) * 100}%`,
		width: `${((element.width || page.width) / page.width) * 100}%`,
		textAlign: elementAlign(element),
		display: "flex",
		alignItems: elementValign(element) === "top" ? "flex-start" : elementValign(element) === "bottom" ? "flex-end" : "center",
		overflow: "hidden",
	};

	if (element.type === "image") {
		const height = element.height ? `${(element.height / page.height) * 100}%` : "12%";
		const src = coverElementImageSrc(element, logos, brandLogo);
		const label = element.binding === "brandLogo" ? "1st Light logo" : element.name ? element.name : "Image";
		return (
			<div
				style={{ ...style, height }}
				className={`flex items-center justify-center overflow-hidden ${src ? "" : "text-[9px] text-stone-500 px-1 text-center"}`}>
				{src ? <img src={src} alt="" className="w-full h-full object-contain" style={{ objectPosition: cssObjectPosition(element) }} /> : label}
			</div>
		);
	}

	return (
		<p
			style={{
				...style,
				height: element.height ? `${(element.height / page.height) * 100}%` : undefined,
				fontFamily: coverFontFamily(element.font),
				fontWeight: String(element.font).includes("Bold") ? 700 : 400,
				fontSize: `${((element.size || 12) / page.width) * 100}cqw`,
				lineHeight: 1.15,
				margin: 0,
			}}
			className="break-words">
			<span className="w-full">{displayText(element, { reportName, dateString })}</span>
		</p>
	);
};

const CoverSheet = ({ instructions, reportName, dateString, logos, brandLogo }) => {
	const page = pageMetrics(instructions?.page);
	const elements = instructions?.elements || [];
	return (
		<div
			className="mx-auto bg-white shadow-md border border-stone-300 relative shrink-0 overflow-hidden w-full max-w-[480px]"
			style={{
				...SHEET_FONT,
				aspectRatio: `${page.width} / ${page.height}`,
				containerType: "inline-size",
			}}>
			{elements.map((element) => (
				<CoverElement
					key={element.id}
					element={element}
					page={page}
					reportName={reportName}
					dateString={dateString}
					logos={logos}
					brandLogo={brandLogo}
				/>
			))}
			<p className="absolute top-2 left-2 text-[9px] uppercase tracking-wide text-stone-400 bg-white/80 px-1">Cover</p>
		</div>
	);
};

/** Match ReportModal createTitlePage — centered project name ~1/3 down the page. */
const DEMO_PROJECT_NAME = "Demo Project";
const ProjectTitleSheet = ({ title = DEMO_PROJECT_NAME, footerDate, pageLabel, pageMargin = 10, lineHeight = 1.2 }) => (
	<PdfSheet footerDate={footerDate} pageLabel={pageLabel} pageMargin={pageMargin} lineHeight={lineHeight}>
		<p
			className="absolute left-0 right-0 m-0 text-center text-black break-words"
			style={{
				top: Math.max(0, PAGE_HEIGHT / 3 - pageMargin),
				fontSize: 24,
				lineHeight: 1.15,
			}}>
			{title}
		</p>
	</PdfSheet>
);

const packBlocks = (nodes, blocks, contentHeight, options = {}) => {
	const thresholdRatio = Math.max(0, Math.min(50, Number(options.newContentThreshold) || 0)) / 100;
	const minRemain = contentHeight * thresholdRatio;
	const sectionSpace = Math.max(0, Number(options.sectionSpace) || 0);
	const pages = [];
	let page = [];
	let used = 0;
	blocks.forEach((block, index) => {
		const height = blockHeight(nodes[index]);
		const forceBreak = block.pageBreak && page.length > 0;
		let breakBefore = forceBreak;

		if (page.length > 0 && !breakBefore) {
			const remaining = contentHeight - used;
			const isNewContent = block.type === "title" || block.type === "field";
			// Mid-page section titles get "above section" spacing; threshold must use where
			// the visible content starts, not where the empty margin starts.
			const topPad = block.type === "title" ? sectionSpace : 0;
			const remainingAtContent = remaining - topPad;
			if (used + height > contentHeight) breakBefore = true;
			if (minRemain > 0 && isNewContent && remainingAtContent < minRemain) breakBefore = true;

			// Keep section title with the next field — avoid a lone heading at the bottom.
			if (!breakBefore && block.type === "title") {
				const next = blocks[index + 1];
				const nextHeight = next && next.type === "field" ? blockHeight(nodes[index + 1]) : 0;
				if (nextHeight > 0) {
					const togetherHeight = height + nextHeight;
					const remainingAfterTitle = contentHeight - used - height;
					const fieldBelowThreshold = minRemain > 0 && remainingAfterTitle < minRemain;
					const togetherOverflows = used + togetherHeight > contentHeight;
					// If the pair fits on an empty page, move both together instead of orphaning the title.
					if ((togetherOverflows || fieldBelowThreshold) && togetherHeight <= contentHeight) {
						breakBefore = true;
					}
				}
			}
		}

		if (breakBefore) {
			pages.push(page);
			page = [];
			used = 0;
		}
		page.push(block);
		used += height;
	});
	if (page.length > 0) pages.push(page);
	return pages.length > 0 ? pages : [blocks];
};

const pageSignature = (pages) => pages.map((page) => page.map((block) => block.key).join(",")).join("|");

/** Match ReportModal TOC_STYLE — title, rule bar, project groups, dotted leaders, section page numbers. */
const TOC_PREVIEW = {
	titleSize: 18,
	groupSize: 16,
	entrySize: 14,
	entryGap: 18,
	groupGapBefore: 8,
	titleRuleGapAbove: 10,
	titleRuleThickness: 1.5,
	titleRuleGapBelow: 8,
};

/**
 * Contents pages sit after the cover and are included in Page X of Y (same as generated PDF).
 * Layout matches mergeReportWithSingleToc: bold project/group titles, then section rows with leaders.
 */
const ContentsPreview = ({ entries, footerDate, pageMargin, lineHeight, tocPageCount, totalNumberedPages, onFocus, onLayout }) => {
	const measureRef = useRef(null);
	const blocks = [{ key: "contents-title", type: "title" }, ...entries.map((entry) => ({ ...entry, type: entry.type || "entry" }))];
	const contentKey = `${pageMargin}|${lineHeight}|${tocPageCount}|${entries
		.map((entry) => `${entry.key}:${entry.type || "entry"}:${entry.page ?? ""}`)
		.join("|")}`;
	const [pages, setPages] = useState([blocks]);
	const reported = useRef("");

	useLayoutEffect(() => {
		const host = measureRef.current;
		if (!host) return;
		const measure = () => {
			const nodes = [...host.children];
			const next = packBlocks(nodes, blocks, contentHeightFor(pageMargin));
			setPages((current) => (pageSignature(current) === pageSignature(next) ? current : next));
			const sig = pageSignature(next);
			if (sig !== reported.current) {
				reported.current = sig;
				onLayout?.(next.length);
			}
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(host);
		return () => observer.disconnect();
		// contentKey already covers the entries that change row heights
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentKey]);

	const renderEntry = (block, prior) => {
		if (block.type === "title") {
			return (
				<div key={block.key} className="m-0" style={{ marginBottom: TOC_PREVIEW.titleRuleGapBelow }}>
					<p className="font-bold text-black m-0" style={{ fontSize: TOC_PREVIEW.titleSize, lineHeight: 1.15 }}>
						Table of Contents
					</p>
					<div
						aria-hidden
						className="bg-black w-full"
						style={{
							marginTop: TOC_PREVIEW.titleRuleGapAbove,
							height: TOC_PREVIEW.titleRuleThickness,
						}}
					/>
				</div>
			);
		}
		if (block.type === "group") {
			// Match generated TOC: no extra gap after the title rule or at the top of a continued page.
			const gapBefore = prior && prior.type !== "title" ? TOC_PREVIEW.groupGapBefore : 0;
			return (
				<p
					key={block.key}
					className="font-bold text-black m-0 break-words"
					style={{
						fontSize: TOC_PREVIEW.groupSize,
						lineHeight: 1.15,
						marginTop: gapBefore,
						marginBottom: 4,
					}}>
					{block.name}
				</p>
			);
		}
		return (
			<button
				key={block.key}
				type="button"
				className="w-full flex items-baseline gap-1.5 text-left rounded px-0.5 hover:bg-amber-50/70"
				style={{ fontSize: TOC_PREVIEW.entrySize, marginBottom: Math.max(0, TOC_PREVIEW.entryGap - TOC_PREVIEW.entrySize), lineHeight: 1.15 }}
				onClick={() => onFocus?.(block.sectionKey)}>
				<span className="min-w-0 break-words text-black">{block.name}</span>
				<span
					aria-hidden
					className="flex-1 min-w-4 overflow-hidden self-end text-stone-500"
					style={{
						letterSpacing: 1.5,
						fontSize: TOC_PREVIEW.entrySize,
						lineHeight: 1,
						height: TOC_PREVIEW.entrySize * 0.85,
					}}>
					{".".repeat(120)}
				</span>
				<span className="shrink-0 tabular-nums text-black">{block.page}</span>
			</button>
		);
	};

	return (
		<div className="relative w-full max-w-[480px] flex flex-col items-center gap-6">
			{pages.map((pageBlocks, pageIndex) => (
				<PdfSheet
					key={`contents-${pageIndex}`}
					footerDate={footerDate}
					pageLabel={`Page ${pageIndex + 1} of ${totalNumberedPages}`}
					pageMargin={pageMargin}
					lineHeight={lineHeight}>
					<div>{pageBlocks.map((block, index) => renderEntry(block, pageBlocks[index - 1]))}</div>
				</PdfSheet>
			))}
			<MeasureHost measureRef={measureRef} pageMargin={pageMargin} lineHeight={lineHeight}>
				{blocks.map((block, index) => renderEntry(block, blocks[index - 1]))}
			</MeasureHost>
		</div>
	);
};

const BodyPreview = ({
	sections,
	showSectionSeparators,
	showFieldLines,
	sectionFontSize,
	fieldNameFontSize,
	fieldDataFontSize,
	sectionSpace,
	fieldSpace,
	nameGap,
	pageMargin,
	lineHeight,
	newContentThreshold = 0,
	footerDate,
	pageNumberOffset = 0,
	totalNumberedPages,
	focusTemplate,
	focusSection,
	focusField,
	onFocus,
	onLayout,
}) => {
	const measureRef = useRef(null);
	const blocks = sections.flatMap((section, sectionIndex) => [
		{
			key: `${section.templateName}|${section.name}|title`,
			type: "title",
			section,
			pageBreak: showSectionSeparators && sectionIndex > 0,
		},
		...section.fields.map((field) => ({
			key: `${section.templateName}|${section.name}|${field.name}`,
			type: "field",
			section,
			field,
		})),
	]);
	const contentKey = `${sectionFontSize}|${fieldNameFontSize}|${fieldDataFontSize}|${sectionSpace}|${fieldSpace}|${nameGap}|${pageMargin}|${lineHeight}|${newContentThreshold}|${showFieldLines ? 1 : 0}|${blocks
		.map((block) => `${block.key}:${block.pageBreak ? 1 : 0}`)
		.join("|")}`;
	const [pages, setPages] = useState([blocks]);
	const reportedLayout = useRef("");

	useLayoutEffect(() => {
		const host = measureRef.current;
		if (!host) return;
		const measure = () => {
			const nodes = [...host.children];
			const next = packBlocks(nodes, blocks, contentHeightFor(pageMargin), {
				newContentThreshold,
				sectionSpace,
			});
			setPages((current) => (pageSignature(current) === pageSignature(next) ? current : next));
			const measured = nodes.length === 0 || nodes.every((node) => node.offsetHeight > 0);
			if (!measured) return;
			const entries = [];
			next.forEach((pageBlocks, pageIndex) => {
				pageBlocks.forEach((block) => {
					if (block.type !== "title") return;
					const section = block.section;
					entries.push({
						key: `${section.templateName}|${section.name}`,
						name: section.name,
						sectionKey: `t:${section.templateName}|s:${section.name}`,
						bodyPage: pageIndex + 1,
					});
				});
			});
			const sig = `${pageSignature(next)}|${entries.map((e) => `${e.key}:${e.bodyPage}`).join(",")}`;
			if (sig !== reportedLayout.current) {
				reportedLayout.current = sig;
				onLayout?.({ entries, pageCount: next.length });
			}
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(host);
		return () => observer.disconnect();
		// contentKey already covers the field list that changes block heights
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentKey]);

	const renderBlock = (block, measure, showDivider = true, firstOnPage = false) => {
		const section = block.section;
		const sectionKey = `t:${section.templateName}|s:${section.name}`;
		const sectionHighlighted = focusSection === section.name && (!focusTemplate || focusTemplate === section.templateName) && !focusField;
		if (block.type === "title") {
			return (
				<button
					key={block.key}
					type="button"
					data-focus-key={measure ? undefined : sectionKey}
					data-template-name={measure ? undefined : section.templateName}
					data-section-name={measure ? undefined : section.name}
					className={`w-full text-left font-bold m-0 rounded cursor-pointer ${
						!measure && sectionHighlighted ? "ring-2 ring-inset ring-amber-300 bg-amber-50/70" : "hover:bg-amber-50/70"
					}`}
					style={{ fontSize: sectionFontSize, marginTop: firstOnPage ? 0 : sectionSpace, lineHeight, overflowWrap: "normal", wordBreak: "normal" }}
					onClick={measure ? undefined : () => onFocus?.(sectionKey)}>
					{section.name}
				</button>
			);
		}
		const field = block.field;
		const focusAttr = `t:${section.templateName}|s:${section.name}|f:${field.name}`;
		return (
			<FieldRow
				key={block.key}
				field={field}
				showDivider={showDivider && showFieldLines}
				nameSize={fieldNameFontSize}
				dataSize={fieldDataFontSize}
				nameGap={nameGap}
				fieldSpace={fieldSpace}
				lineHeight={lineHeight}
				focusAttr={measure ? undefined : focusAttr}
				onSelect={measure ? undefined : onFocus}
				highlighted={
					!measure && focusField === field.name && focusSection === section.name && (!focusTemplate || focusTemplate === section.templateName)
				}
			/>
		);
	};

	return (
		<div className="relative w-full flex flex-col items-center gap-6">
			{pages.map((pageBlocks, pageIndex) => (
				<div key={pageBlocks.map((block) => block.key).join("|")} className="w-full flex flex-col items-center gap-6">
					{pageBlocks[0]?.pageBreak ? (
						<p className="text-[11px] text-stone-600 bg-white/80 border border-stone-300 rounded-full px-3 py-1">Section starts on a new page</p>
					) : null}
					<PdfSheet
						footerDate={footerDate}
						pageLabel={`Page ${pageNumberOffset + pageIndex + 1} of ${totalNumberedPages || pages.length}`}
						pageMargin={pageMargin}
						lineHeight={lineHeight}
						thresholdPercent={newContentThreshold}>
						<div>
							{pageBlocks.map((block, blockIndex) => {
								// Match generated PDF: fieldLines draws a rule before every field, including the first after a section title.
								// Skip "above section" spacing when the section is the first item on the page.
								return renderBlock(block, false, true, blockIndex === 0);
							})}
						</div>
					</PdfSheet>
				</div>
			))}
			<MeasureHost measureRef={measureRef} pageMargin={pageMargin} lineHeight={lineHeight}>
				{blocks.map((block) => renderBlock(block, true))}
			</MeasureHost>
		</div>
	);
};

const findFocusTarget = (root, focusKey, focusTemplate, focusSection, focusField) => {
	if (!root || !focusKey) return null;
	const nodes = [...root.querySelectorAll("[data-focus-key]")];
	const exact = nodes.find((el) => el.getAttribute("data-focus-key") === focusKey);
	if (exact) return exact;

	// Template-only: jump to first included section from that template
	if (focusTemplate && !focusSection) {
		return nodes.find((el) => el.getAttribute("data-template-name") === focusTemplate) || null;
	}

	// Section focused but not rendered (e.g. no included fields) — no target
	if (focusSection && !focusField) {
		return (
			nodes.find(
				(el) => el.getAttribute("data-section-name") === focusSection && (!focusTemplate || el.getAttribute("data-template-name") === focusTemplate),
			) || null
		);
	}

	return null;
};

const ReportPdfPreview = ({
	api,
	token,
	reportName,
	saveName,
	coverImgName,
	coverInstructions,
	templateData,
	pdfSettings: pdfSettingsProp,
	onPdfSettingsChange,
	catalog,
	focusKey,
	focusNonce,
	onFocus,
	onReady,
}) => {
	const scrollRef = useRef(null);
	const [logos, setLogos] = useState([]);
	const [brandLogo, setBrandLogo] = useState("");
	const [coverFetchDone, setCoverFetchDone] = useState(false);
	const [assetsReady, setAssetsReady] = useState(false);
	const [bodyReady, setBodyReady] = useState(false);
	const [tocEntries, setTocEntries] = useState([]);
	const [bodyPageCount, setBodyPageCount] = useState(1);
	const [tocPageCount, setTocPageCount] = useState(1);
	const [showTypeSizeHelp, setShowTypeSizeHelp] = useState(false);
	const [showSpacingHelp, setShowSpacingHelp] = useState(false);
	const revealed = useRef(false);
	const pdfSettings = normalizePdfSettings(pdfSettingsProp || DEFAULT_PDF_SETTINGS);
	const {
		sectionSeparators: showSectionSeparators,
		fieldLines: showFieldLines,
		sectionFontSize,
		fieldNameFontSize,
		fieldDataFontSize,
		sectionSpace,
		fieldSpace,
		nameGap,
		pageMargin,
		lineHeight,
		newContentThreshold,
	} = pdfSettings;
	const patchPdfSettings = (patch) => {
		onPdfSettingsChange?.(normalizePdfSettings({ ...pdfSettings, ...patch }));
	};

	useEffect(() => {
		if (!api || !token) {
			setCoverFetchDone(true);
			return;
		}
		let cancelled = false;
		const instructions = ensureCoverInstructions({ reportName, coverImgName, coverInstructions }).coverInstructions;
		setCoverFetchDone(false);
		setAssetsReady(false);
		fetch(`${api}/report/logos`, {
			method: "GET",
			headers: { Authorization: "Bearer " + token },
		})
			.then((res) => (res.ok ? res.json() : null))
			.then(async (data) => {
				if (cancelled) return;
				if (data) {
					const nextLogos = data.logos || [];
					const nextBrand = data.brandLogo || "";
					setLogos(nextLogos);
					setBrandLogo(nextBrand);
					const srcs = [
						...new Set(
							[
								nextBrand,
								...(instructions.elements || [])
									.filter((element) => element.type === "image")
									.map((element) => coverElementImageSrc(element, nextLogos, nextBrand)),
							].filter(Boolean),
						),
					];
					await Promise.all(srcs.map(preloadImage));
				}
				if (!cancelled) setCoverFetchDone(true);
			})
			.catch(() => {
				if (!cancelled) setCoverFetchDone(true);
			});
		return () => {
			cancelled = true;
		};
		// Cover images are preloaded once for the first view. Later cover edits update in place.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, token]);

	// After logo state commits and CoverSheet mounts <img>s, wait for those before revealing.
	useEffect(() => {
		if (!coverFetchDone) return;
		let cancelled = false;
		(async () => {
			await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
			if (cancelled) return;
			await waitForDomImages(scrollRef.current);
			if (!cancelled) setAssetsReady(true);
		})();
		return () => {
			cancelled = true;
		};
	}, [coverFetchDone, logos, brandLogo]);

	const model = buildPreviewModel({ reportName, saveName, coverImgName, templateData, catalog });
	const hasContent = model.sections.length > 0;
	const layoutReady = !hasContent || bodyReady;
	const previewReady = assetsReady && layoutReady;
	// Real merge: cover (unnumbered) → TOC → project title page → body.
	const projectTitlePageCount = hasContent ? 1 : 0;
	const totalNumberedPages = Math.max(1, (hasContent ? tocPageCount : 0) + projectTitlePageCount + bodyPageCount);
	const bodyPageOffset = tocPageCount + projectTitlePageCount;
	// Real PDF TOC puts the project/group title above its sections (no page number on the group row).
	// Preview uses a sample label so builders don't think the report title is what prints there.
	const tocDisplayEntries =
		tocEntries.length === 0
			? []
			: [
					{
						key: "group:demo-project",
						type: "group",
						name: DEMO_PROJECT_NAME,
					},
					...tocEntries.map((entry) => ({
						...entry,
						type: "entry",
						page: bodyPageOffset + entry.bodyPage,
					})),
			  ];

	useEffect(() => {
		if (!hasContent) {
			setTocEntries([]);
			setBodyPageCount(1);
			setTocPageCount(0);
			setBodyReady(true);
		}
	}, [hasContent]);

	useEffect(() => {
		if (!previewReady || revealed.current) return;
		let cancelled = false;
		(async () => {
			await waitForDomImages(scrollRef.current);
			if (cancelled || revealed.current) return;
			revealed.current = true;
			onReady?.();
		})();
		return () => {
			cancelled = true;
		};
	}, [previewReady, onReady]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setCoverFetchDone(true);
			setAssetsReady(true);
			setBodyReady(true);
		}, 8000);
		return () => clearTimeout(timer);
	}, []);

	const focusTemplate = focusKey?.startsWith("t:") ? focusKey.split("|")[0].slice(2) : null;
	const focusSection = focusKey?.includes("|s:") ? focusKey.split("|s:")[1]?.split("|")[0] : null;
	const focusField = focusKey?.includes("|f:") ? focusKey.split("|f:")[1] : null;

	useEffect(() => {
		if (!focusKey) return;
		const root = scrollRef.current;
		let cancelled = false;
		// Wait a frame so newly included fields exist in the DOM before scrolling
		const frame = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (cancelled) return;
				const target = findFocusTarget(root, focusKey, focusTemplate, focusSection, focusField);
				if (!target) return;
				target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
			});
		});
		return () => {
			cancelled = true;
			cancelAnimationFrame(frame);
		};
	}, [focusKey, focusNonce, focusTemplate, focusSection, focusField, templateData]);

	return (
		<div className="flex h-full min-h-0 gap-3">
			<div className="flex flex-col flex-1 min-w-0 min-h-0">
				<div className="shrink-0 flex flex-wrap items-center justify-between gap-2 mb-2">
					<div>
						<p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">PDF preview</p>
						<p className="text-[11px] text-stone-400">A4 · Times · click a field to jump · click again to clear</p>
					</div>
					{model.coverImgName ? <p className="text-[11px] text-stone-500">Cover: {model.coverImgName} (separate cover PDF)</p> : null}
				</div>

				<div
					ref={scrollRef}
					className="flex-1 min-h-0 overflow-y-auto bg-stone-300/70 rounded border border-stone-400 p-4"
					onClick={(e) => {
						if (e.target === e.currentTarget) onFocus?.(null);
					}}>
					<div
						className="flex flex-col items-center gap-6 pb-4 min-h-full"
						onClick={(e) => {
							if (e.target === e.currentTarget) onFocus?.(null);
						}}>
						<CoverSheet
							instructions={ensureCoverInstructions({ reportName, coverImgName, coverInstructions }).coverInstructions}
							reportName={model.reportName}
							dateString={model.dateString}
							logos={logos}
							brandLogo={brandLogo}
						/>

						{!hasContent ? (
							<PdfSheet footerDate={model.dateString} pageLabel="Page 1 of 1" pageMargin={pageMargin} lineHeight={lineHeight}>
								<p className="font-bold m-0 break-words" style={{ fontSize: 25 }}>
									{model.reportName}
								</p>
								<p className="m-0 mt-3 break-words" style={{ fontSize: 20 }}>
									This report template is empty or the odoo project has not been assigned to any of the templates
								</p>
							</PdfSheet>
						) : (
							<>
								{tocDisplayEntries.length > 0 ? (
									<ContentsPreview
										entries={tocDisplayEntries}
										footerDate={model.dateString}
										pageMargin={pageMargin}
										lineHeight={lineHeight}
										tocPageCount={tocPageCount}
										totalNumberedPages={totalNumberedPages}
										onFocus={onFocus}
										onLayout={(count) => setTocPageCount(Math.max(1, count))}
									/>
								) : null}
								<ProjectTitleSheet
									title={DEMO_PROJECT_NAME}
									footerDate={model.dateString}
									pageLabel={`Page ${tocPageCount + 1} of ${totalNumberedPages}`}
									pageMargin={pageMargin}
									lineHeight={lineHeight}
								/>
								<BodyPreview
									sections={model.sections}
									showSectionSeparators={showSectionSeparators}
									showFieldLines={showFieldLines}
									sectionFontSize={sectionFontSize}
									fieldNameFontSize={fieldNameFontSize}
									fieldDataFontSize={fieldDataFontSize}
									sectionSpace={sectionSpace}
									fieldSpace={fieldSpace}
									nameGap={nameGap}
									pageMargin={pageMargin}
									lineHeight={lineHeight}
									newContentThreshold={newContentThreshold}
									footerDate={model.dateString}
									pageNumberOffset={bodyPageOffset}
									totalNumberedPages={totalNumberedPages}
									focusTemplate={focusTemplate}
									focusSection={focusSection}
									focusField={focusField}
									onFocus={onFocus}
									onLayout={({ entries, pageCount }) => {
										setTocEntries(entries);
										setBodyPageCount(Math.max(1, pageCount));
										setBodyReady(true);
									}}
								/>
							</>
						)}
					</div>
				</div>
			</div>
			<aside className="shrink-0 w-[240px] min-h-0 bg-white border border-stone-200 rounded flex flex-col overflow-hidden">
				<div className="shrink-0 px-3 py-2 border-b border-stone-100">
					<p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">Settings</p>
				</div>
				<div className="flex-1 min-h-0 overflow-y-auto p-3">
					<div className="mb-2 bg-stone-200 text-black rounded px-2 py-1">
						<p className="text-sm uppercase tracking-wide font-bold">Layout</p>
					</div>
					<label className="flex items-start gap-2 text-sm cursor-pointer select-none">
						<input
							type="checkbox"
							className="mt-0.5"
							checked={showSectionSeparators}
							onChange={(e) => patchPdfSettings({ sectionSeparators: e.target.checked })}
						/>
						<span>
							<span className="block text-stone-800">Section separators</span>
							<span className="block text-[11px] text-stone-400 mt-0.5 leading-snug">
								Start each section on a new page. Turn this off to continue on the current page.
							</span>
						</span>
					</label>
					<label className="flex items-start gap-2 text-sm cursor-pointer select-none mt-4">
						<input
							type="checkbox"
							className="mt-0.5"
							checked={showFieldLines}
							onChange={(e) => patchPdfSettings({ fieldLines: e.target.checked })}
						/>
						<span>
							<span className="block text-stone-800">Lines between fields</span>
							<span className="block text-[11px] text-stone-400 mt-0.5 leading-snug">
								Show the line above each field, including under section names.
							</span>
						</span>
					</label>
					<div className="mt-4 flex flex-col gap-2">
						<div className="flex items-center justify-between bg-stone-200 text-black rounded px-2 py-1">
							<p className="text-sm uppercase tracking-wide font-bold">Type size</p>
							<button
								type="button"
								aria-expanded={showTypeSizeHelp}
								aria-label="How to change type size"
								onClick={() => setShowTypeSizeHelp((open) => !open)}
								className="w-5 h-5 rounded-full border border-stone-400 bg-white text-[11px] font-bold leading-none text-black hover:bg-stone-100">
								i
							</button>
						</div>
						{showTypeSizeHelp ? (
							<p className="text-xs text-stone-500 leading-snug">
								Drag a label left or right to change the size. You can also scroll over the row, or type a number from 8 to 72.
							</p>
						) : null}
						<FontSizeField label="Section name" value={sectionFontSize} onChange={(sectionFontSize) => patchPdfSettings({ sectionFontSize })} />
						<FontSizeField label="Field name" value={fieldNameFontSize} onChange={(fieldNameFontSize) => patchPdfSettings({ fieldNameFontSize })} />
						<FontSizeField label="Field data" value={fieldDataFontSize} onChange={(fieldDataFontSize) => patchPdfSettings({ fieldDataFontSize })} />
					</div>
					<div className="mt-4 flex flex-col gap-2">
						<div className="flex items-center justify-between bg-stone-200 text-black rounded px-2 py-1">
							<p className="text-sm uppercase tracking-wide font-bold">Spacing</p>
							<button
								type="button"
								aria-expanded={showSpacingHelp}
								aria-label="How to change spacing"
								onClick={() => setShowSpacingHelp((open) => !open)}
								className="w-5 h-5 rounded-full border border-stone-400 bg-white text-[11px] font-bold leading-none text-black hover:bg-stone-100">
								i
							</button>
						</div>
						{showSpacingHelp ? (
							<p className="text-xs text-stone-500 leading-snug">
								Drag a label left or right, scroll over the row, or type a number. These start at the generated report’s spacing. Line height
								moves by 0.1.
							</p>
						) : null}
						<FontSizeField
							label="Above section"
							value={sectionSpace}
							onChange={(sectionSpace) => patchPdfSettings({ sectionSpace })}
							min={0}
							max={48}
						/>
						<FontSizeField label="Between fields" value={fieldSpace} onChange={(fieldSpace) => patchPdfSettings({ fieldSpace })} min={0} max={48} />
						<FontSizeField label="Between name and data" value={nameGap} onChange={(nameGap) => patchPdfSettings({ nameGap })} min={0} max={48} />
						<FontSizeField label="Page margin" value={pageMargin} onChange={(pageMargin) => patchPdfSettings({ pageMargin })} min={0} max={48} />
						<FontSizeField
							label="Line height"
							value={lineHeight}
							onChange={(lineHeight) => patchPdfSettings({ lineHeight })}
							min={1}
							max={2}
							step={0.1}
						/>
						<FontSizeField
							label="New content threshold %"
							value={newContentThreshold}
							onChange={(newContentThreshold) => patchPdfSettings({ newContentThreshold })}
							min={0}
							max={50}
						/>
						<p className="text-[11px] text-stone-400 leading-snug -mt-1">
							Don&apos;t start a section or field when less than this % of the page is left (0–50). 0 turns the start rule off but still shows the
							guide line. Section titles also stay with their first field so they are not left alone at the bottom of a page.
						</p>
					</div>
				</div>
			</aside>
		</div>
	);
};

export default ReportPdfPreview;
